import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Tree } from '@blueprintjs/core';
import { Box, Button, Popover } from 'core/components';
import { InputGroup } from 'core/form';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { getDimensionLabel as resolveDimensionLabel, getGroupedDimensions } from 'app/stores/ude/measurementTransforms';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import TreeWrapper from 'app/views/boards/panels/ude/TreeWrapper';
import {
  filterTreeNodes,
  mergeExpandedState,
  transformMeasurementsToBlueprintTree
} from 'app/views/boards/panels/ude/metricsTreeData';
import { UdeTreeNode, updateNode } from 'app/views/boards/panels/ude/treeUtils';

interface DimensionSelectorProps {
  value: string;
  onChange: (value: string) => void;
  measurement: string | null;
  measurements: Measurement[];
  /** When provided, only dimension nodes whose `value` is in this list will appear in the tree. */
  allowedValues?: string[];
  minimal?: boolean;
}

const PopoverContent = styled.div`
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DimensionTreeSelector = observer(
  ({ value, onChange, measurement, measurements, allowedValues, minimal }: DimensionSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [treeContents, setTreeContents] = useState<UdeTreeNode[]>([]);
    const [baseTree, setBaseTree] = useState<UdeTreeNode[]>([]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      setSearch('');
    }, []);

    const handleDirectionalSelect = useCallback(
      (selection: UdeTreeNode) => {
        if (selection.value) {
          onChange(selection.value);
          handleClose();
        }
      },
      [onChange, handleClose]
    );

    const initializeTree = useCallback(() => {
      if (!measurement) {
        return;
      }

      // Build tree from measurements - pass our handler for directional dimension buttons
      const allTree = transformMeasurementsToBlueprintTree(measurements);

      // Filter to just this measurement's dimensions
      const findMeasurementNode = (nodes: UdeTreeNode[], path: string): UdeTreeNode | null => {
        for (const node of nodes) {
          if (node.id === path) {
            return node;
          }
          if (node.childNodes?.length) {
            const found = findMeasurementNode(node.childNodes, path);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      const measurementNode = findMeasurementNode(allTree, measurement);
      if (!measurementNode) {
        return;
      }

      // Filter to dimensions only - simple approach: just filter the children to type === 'dimension'
      const allDimensionChildren = (measurementNode.childNodes || []).filter((child) => child.type === 'dimension');
      const dimensionChildren =
        allowedValues && allowedValues.length > 0
          ? allDimensionChildren.filter((child) => {
              // Direct match on the node's own value
              if (child.value !== undefined && allowedValues.includes(child.value)) {
                return true;
              }
              // For directional/grouped nodes (e.g. src_as + dst_as merged into a single "as" node),
              // the grouping key won't appear in allowedValues — check each direction's actual key instead.
              if (child.isDirectionalNode) {
                const directions = (child.nodeData as { Directions?: Record<string, { key?: string }> } | undefined)
                  ?.Directions;
                return directions
                  ? Object.values(directions).some((dir) => dir.key !== undefined && allowedValues.includes(dir.key))
                  : false;
              }
              return false;
            })
          : allDimensionChildren;

      // Create a single root node containing only dimensions
      const filtered: UdeTreeNode[] = [
        {
          ...measurementNode,
          childNodes: dimensionChildren,
          isExpanded: true
        }
      ];

      setBaseTree(filtered);
      setTreeContents(filtered);
    }, [measurement, measurements, handleDirectionalSelect, allowedValues]);

    const handleOpen = useCallback(() => {
      initializeTree();
      setIsOpen(true);
    }, [initializeTree]);

    const handleSearchChange = useCallback(
      (searchValue: string) => {
        setSearch(searchValue);
        if (searchValue) {
          const filtered = mergeExpandedState(baseTree, filterTreeNodes(baseTree, searchValue.toLowerCase().trim()));
          setTreeContents(filtered);
        } else {
          setTreeContents(baseTree);
        }
      },
      [baseTree]
    );

    const handleNodeClick = useCallback(
      (node: UdeTreeNode) => {
        // Skip directional nodes - they handle selection via their own buttons
        if (node.isDirectionalNode) {
          return;
        }

        // If this is a selectable dimension, select it
        if (node.type === 'dimension' && node.value) {
          onChange(node.value);
          handleClose();
          return;
        }

        // Otherwise, toggle expand/collapse for parent nodes
        if (node.childNodes?.length) {
          setTreeContents((prev) =>
            prev.map((n) => updateNode(n, node, (childNode) => ({ ...childNode, isExpanded: !childNode.isExpanded })))
          );
        }
      },
      [onChange, handleClose]
    );

    const handleNodeCollapse = useCallback((node: UdeTreeNode) => {
      setTreeContents((prev) =>
        prev.map((n) => updateNode(n, node, (childNode) => ({ ...childNode, isExpanded: false })))
      );
    }, []);

    const handleNodeExpand = useCallback((node: UdeTreeNode) => {
      setTreeContents((prev) =>
        prev.map((n) => updateNode(n, node, (childNode) => ({ ...childNode, isExpanded: true })))
      );
    }, []);

    const getDimensionLabel = useCallback(() => {
      if (!value) {
        return 'Select dimension...';
      }
      if (!measurement || !measurements.some((m) => m.measurement === measurement)) {
        return value;
      }
      const measurementHash: MeasurementHash = Object.fromEntries(measurements.map((m) => [m.measurement, m]));
      return resolveDimensionLabel(value, getGroupedDimensions(measurement, measurementHash));
    }, [value, measurement, measurements]);

    const displayLabel = getDimensionLabel();
    const treeChildContents = treeContents?.[0]?.childNodes || [];

    return (
      <Popover
        isOpen={isOpen}
        onClose={handleClose}
        position="bottom-left"
        boundary="viewport"
        usePortal
        canEscapeKeyClose
        enforceFocus={false}
        content={
          <PopoverContent>
            <InputGroup
              type="search"
              fill
              autoFocus
              leftIcon="search"
              placeholder="Search dimensions..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
            />
            <Box flex={1} borderTop="thin" overflow="auto" m="0 -12px -12px -12px">
              <TreeWrapper>
                <Tree
                  contents={treeChildContents}
                  onNodeClick={handleNodeClick}
                  onNodeCollapse={handleNodeCollapse}
                  onNodeExpand={handleNodeExpand}
                />
              </TreeWrapper>
            </Box>
          </PopoverContent>
        }
      >
        <Button
          fill
          small
          minimal={minimal}
          textAlign="left"
          text={displayLabel}
          rightIcon="caret-down"
          onClick={handleOpen}
          style={{ justifyContent: 'space-between', textAlign: 'left' }}
        />
      </Popover>
    );
  }
);

export default DimensionTreeSelector;
